import React, { useState, useEffect } from "react";
import { Offcanvas, Row, Col } from "react-bootstrap";
import swal from "sweetalert";
import useApiServices from "../../../utils/useApiServices";

import {
  FETCH_ALL_MERCHANT_VALIDATIONS,
  FETCH_MERCHANT_FOR_MASTERS_FORMS,
  CHECK_DUPLICATE_ENTRIES,
  FETCH_PAYOUT_MARTER_PROVIDERS,
} from "../../../utils/constant";
import DynamicInput from "../../common/DynamicInput";
import { payoutProviderConfig } from "../../../utils/payoutProviders.config";

const PayoutMasterForm = ({ show, onHide, setRefresh, refresh }) => {
  const { apiCallFnforPost, apiCallFnforGet } = useApiServices();

  const [merchantList, setMerchantList] = useState([]);
  const [vehicleList, setVehicleList] = useState([]);

  const [selectedPayout, setSelectedPayout] = useState("");
  const [transferType, setTransferType] = useState("imps");

  const [selectedMid, setSelectedMid] = useState("");
  const [merchantMasterId, setMerchantMasterId] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [providerList, setProviderList] = useState([]);
  const [formValues, setFormValues] = useState({});

  const initDropdownData = async () => {
    try {
      const [merchantRes, vehicleRes, providerRes] = await Promise.allSettled([
        apiCallFnforGet(FETCH_ALL_MERCHANT_VALIDATIONS),
        apiCallFnforPost(FETCH_MERCHANT_FOR_MASTERS_FORMS, { serviceType: "PAYOUT" }),
        apiCallFnforGet(FETCH_PAYOUT_MARTER_PROVIDERS),
      ]);

      if (merchantRes.status === "fulfilled" && merchantRes.value?.statusCode === 200) {
        setMerchantList(merchantRes.value.data || []);
      } else {
        setMerchantList([]);
        swal("Error!", "Failed to load merchant list", "error");
      }

      if (vehicleRes.status === "fulfilled" && vehicleRes.value?.statusCode === 200) {
        setVehicleList(vehicleRes.value.data || []);
      } else {
        setVehicleList([]);
        swal("Error!", "Failed to load vehicle list", "error");
      }

      if (providerRes.status === "fulfilled" && providerRes.value?.statusCode === 200) {
        setProviderList(providerRes.value.data || []);
      } else {
        setProviderList([]);
        swal("Error!", "Failed to load payout providers", "error");
      }

    } catch (err) {
      console.error("Dropdown init error:", err);
      swal("Error!", "Something went wrong while initializing dropdown data", "error");
    }
  };

  useEffect(() => {
    initDropdownData();
  }, []);

  const setValue = (key, val) => {
    setFormValues((prev) => ({ ...prev, [key]: val }));
  };

  const validateForm = () => {
    const config = payoutProviderConfig[selectedPayout];
    if (!config) return false;

    for (let f of config.fields) {
      if (f.required && !formValues[f.name]) {
        swal("Alert!", `Please enter ${f.label}`, "error");
        return false;
      }
      if (f.type === "mobile" && formValues[f.name].length !== 10) {
        swal("Alert!", "Invalid Mobile Number", "error");
        return false;
      }
    }
    return true;
  };

  const checkDuplicateEntry = async () => {
    try {
      const response = await apiCallFnforPost(CHECK_DUPLICATE_ENTRIES, {
        mid: selectedMid,
        transferType: transferType.toUpperCase(),
      });
      console.log("Response from checkDuplicateEntry:", response.data);
      if (
        response.statusCode === 200 &&
        response.status ===
        "Merchant is already associated with this transfer type"
      ) {
        console.log("Duplicate entry detected");
        swal({
          title: "Duplicate Entry!",
          text: response.status,
          icon: "warning",
        });
        return true;
      } else if (
        response.statusCode === 200 &&
        response.status === "Merchant is not associated with this transfer type"
      ) {
        return false;
      } else {
        swal({
          title: "Not Associated!",
          text: response.status,
          icon: "info",
        });
      }
    } catch (error) {
      console.error("Error checking entry:", error);
    }
    console.log("Default case - No matching condition found");
    return false;
  };

  const handleSubmit = async () => {
    // Top level validation
    if (!selectedPayout) return swal("Alert!", "Please select a Payout Provider", "error");
    if (!selectedMid) return swal("Alert!", "Please select a Merchant", "error");
    if (!vehicle) return swal("Alert!", "Please select a Vehicle", "error");
    if (!transferType) return swal("Alert!", "Please select a Transfer Type", "error");

    const isDuplicateEntry = await checkDuplicateEntry();

    if (isDuplicateEntry) {
      return;
    }

    // Field validation
    if (!validateForm()) return;

    // get provider config
    const config = payoutProviderConfig[selectedPayout];

    // build payload using provider-specific DTO
    const payload = config.payload(formValues, {
      mid: selectedMid,
      vehicle,
      transferType,
      masterMerchantId: merchantMasterId,
    });

    // get provider-specific endpoint
    const apiUrl = config.apiUrl;

    const res = await apiCallFnforPost(apiUrl, payload);

    if (res.statusCode === 200) {
      swal("Success!", res.message, "success");
      resetForm();
    } else {
      swal("Error!", res.message, "error");
    }
  };

  const resetForm = () => {
    setFormValues({});
    setSelectedPayout("");
    setSelectedMid("");
    setVehicle("");
    setMerchantMasterId("");
    setRefresh(!refresh);
    onHide();
  };

  return (
    <Offcanvas show={show} onHide={resetForm} placement="end" style={{ width: "70vw" }}>
      <Offcanvas.Header closeButton>
        <h4>Payout Master Form</h4>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <div className="container-fluid">

          {/* Provider Dropdown */}
          <Row className="mb-3">
            <Col>
              <label>Select Payout</label>
              <select
                className="form-control"
                value={selectedPayout}
                onChange={(e) => setSelectedPayout(e.target.value)}
              >
                <option value="">Select</option>

                {providerList?.map(
                  (name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  )
                )}
              </select>
            </Col>

            <Col>
              <label>Transfer Type</label>
              <select
                className="form-control"
                value={transferType}
                onChange={(e) => setTransferType(e.target.value)}
              >
                <option value="imps">IMPS</option>
              </select>
            </Col>
          </Row>

          {/* Merchant */}
          <Row className="mb-3">
            <Col>
              <label>Select Merchant</label>
              <select
                className="form-control"
                onChange={(e) => setSelectedMid(e.target.value)}
                value={selectedMid}
              >
                <option value="">Select Merchant</option>
                {merchantList.map((m) => (
                  <option key={m.mid} value={m.mid}>
                    {m.merchantName} — {m.mid}
                  </option>
                ))}
              </select>
            </Col>
            <Col>
              <label>Merchant MID</label>
              <input
                type="text"
                className="form-control"
                value={selectedMid}
                readOnly
              />
            </Col>
          </Row>

          {/* Readonly Fields */}
          <Row className="mb-3">
            <Col>
              <label>Select Vehicle</label>
              <select
                className="form-control"
                value={vehicle}
                onChange={(e) => {
                  const opt = vehicleList.find((v) => v.id == e.target.value);
                  setVehicle(e.target.value);
                  setMerchantMasterId(opt?.masterMerchantId || "");
                }}
              >
                <option value="">Select Vehicle</option>
                {vehicleList.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.vehicleName}
                  </option>
                ))}
              </select>
            </Col>

            <Col>
              <label>Master Merchant Name</label>
              <input
                type="text"
                className="form-control"
                value={
                  vehicleList.find((x) => x.id == vehicle)?.masterMerchantName || ""
                }
                readOnly
              />
            </Col>
          </Row>

          {/* Dynamic Form Fields */}
          <Row>
            {selectedPayout &&
              payoutProviderConfig[selectedPayout]?.fields.map((field) => (
                <Col xs={12} md={6} lg={4} key={field.name}>
                  <DynamicInput
                    field={field}
                    value={formValues[field.name]}
                    onChange={setValue}
                  />
                </Col>
              ))}
          </Row>

          {/* Submit */}
          <div className="d-flex justify-content-end mt-4">
            <button className="btn btn-primary px-4 py-2" onClick={handleSubmit}>
              Submit
            </button>
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default PayoutMasterForm;